import { z } from 'zod';
import { NewPassword } from './auth.js';
import { HEARD_FROMS, PROVIDER_KINDS, PROVIDER_STATUSES, PROVINCES } from './enums.js';
import { Cuid2, DiscountPercent, Email, IsoDateTime, MoneyCents, optional, PersonName, PolicyVersion, ZaPhone, } from './primitives.js';
/** Provider statuses in which the provider may still edit their own profile (domain.md, Provider status). */
export const PROVIDER_EDITABLE_STATUSES = ['DRAFT', 'PENDING_REVIEW', 'CHANGES_REQUESTED'];
/** Technical caps, so one request cannot write thousands of rows. */
export const MAX_PROVIDER_CATEGORIES = 10;
export const MAX_PROVIDER_SERVICE_AREAS = 100;
export const MAX_PROVIDER_LIST_ITEMS = 20;
const BusinessName = z
    .string()
    .trim()
    .min(2, 'Enter the business name.')
    .max(120, 'Keep this under 120 characters.');
/** One entry in a provider's list of services or payment methods. */
const ListItem = z.string().trim().min(1).max(80, 'Keep each line under 80 characters.');
/**
 * `POST /providers/register` (requirements.md, Provider application; D9).
 * Creates the sign-in and a DRAFT provider. Everything else is filled in
 * afterwards, one step at a time.
 */
export const RegisterProviderInput = z.object({
    contactFirstName: PersonName,
    contactLastName: PersonName,
    email: Email,
    password: NewPassword,
    phone: ZaPhone,
    kind: z.enum(PROVIDER_KINDS, { error: 'Tell us what kind of business you run.' }),
    legalName: BusinessName,
    heardFrom: z.enum(HEARD_FROMS, { error: 'Tell us where you heard about MyHome+.' }),
    acceptPolicies: z.literal(true, {
        error: 'You need to accept the terms and conditions and the privacy policy to sign up.',
    }),
    termsVersion: PolicyVersion,
    privacyVersion: PolicyVersion,
    /** Unticked by default; stored as a consent with a timestamp (POPIA). */
    marketingConsent: z.boolean(),
});
/** 201 from `POST /providers/register`, with the session cookie set. */
export const RegisterProviderResult = z.object({ providerId: Cuid2 });
/** Step 1, `PUT /providers/me/business`: who the business is and how members reach it. */
export const ProviderBusinessInput = z.object({
    contactFirstName: PersonName,
    contactLastName: PersonName,
    phone: ZaPhone,
    /** Where members send quote requests. */
    email: Email,
    legalName: BusinessName,
    tradingName: optional(BusinessName),
    /** CIPC number, like 2015/123456/07. Sole traders may not have one. */
    registrationNo: optional(z.string().trim().max(30, 'Keep this under 30 characters.')),
    /** SARS VAT numbers are 10 digits and start with 4. */
    vatNo: optional(z
        .string()
        .trim()
        .regex(/^4\d{9}$/, 'A VAT number is 10 digits and starts with 4.')),
    /** The year-month-day the business started trading. */
    activeSince: optional(z.iso.date('Enter a date, like 2015-03-01.')),
    basedInAreaId: z.cuid2({ error: 'Choose the suburb or town the business is based in.' }),
});
/** Step 2, `PUT /providers/me/coverage`: trade categories and the areas served. */
export const ProviderCoverageInput = z.object({
    categoryIds: z
        .array(z.cuid2())
        .min(1, 'Choose at least one category.')
        .max(MAX_PROVIDER_CATEGORIES, `Choose up to ${MAX_PROVIDER_CATEGORIES} categories.`),
    serviceAreaIds: z
        .array(z.cuid2())
        .min(1, 'Choose at least one area you work in.')
        .max(MAX_PROVIDER_SERVICE_AREAS, `Choose up to ${MAX_PROVIDER_SERVICE_AREAS} areas.`),
});
/**
 * Step 3, `PUT /providers/me/listing`: what members read on the listing.
 * The hourly rate is for attorneys only; the API rejects it for other kinds.
 */
export const ProviderListingInput = z.object({
    overview: z
        .string()
        .trim()
        .min(1, 'Tell members about the business.')
        .max(2000, 'Keep this under 2 000 characters.'),
    services: z.array(ListItem).max(MAX_PROVIDER_LIST_ITEMS, `List up to ${MAX_PROVIDER_LIST_ITEMS} services.`),
    paymentMethods: z
        .array(ListItem)
        .max(MAX_PROVIDER_LIST_ITEMS, `List up to ${MAX_PROVIDER_LIST_ITEMS} payment methods.`),
    discountPercent: optional(DiscountPercent),
    discountText: optional(z.string().trim().max(120, 'Keep this under 120 characters.')),
    callOutFee: z.boolean({ error: 'Tell members whether you charge a call-out fee.' }),
    responseTimeMinutes: optional(z
        .int()
        .min(1, 'Enter a time in minutes.')
        .max(7 * 24 * 60, 'Enter a time under a week.')),
    emergencyAvailable: z.boolean(),
    hourlyRateCents: optional(MoneyCents),
});
const AreaRef = z.object({ id: Cuid2, name: z.string(), city: z.string(), province: z.enum(PROVINCES) });
const CategoryRef = z.object({ id: Cuid2, name: z.string() });
/** 200 from `GET /providers/me` and the step endpoints: the provider's own profile. */
export const ProviderProfile = z.object({
    id: Cuid2,
    slug: z.string(),
    kind: z.enum(PROVIDER_KINDS),
    status: z.enum(PROVIDER_STATUSES),
    /** Shown to the provider when status is REJECTED. */
    rejectionReason: z.string().nullable(),
    /** The sign-in email, which may differ from the quote email. */
    accountEmail: z.string(),
    emailVerified: z.boolean(),
    contactFirstName: z.string(),
    contactLastName: z.string(),
    phone: z.string(),
    email: z.string(),
    legalName: z.string(),
    tradingName: z.string().nullable(),
    registrationNo: z.string().nullable(),
    vatNo: z.string().nullable(),
    /** YYYY-MM-DD. */
    activeSince: z.string().nullable(),
    basedInArea: AreaRef.nullable(),
    categories: z.array(CategoryRef),
    serviceAreas: z.array(AreaRef),
    overview: z.string().nullable(),
    services: z.array(z.string()),
    paymentMethods: z.array(z.string()),
    discountPercent: z.int().nullable(),
    discountText: z.string().nullable(),
    callOutFee: z.boolean().nullable(),
    responseTimeMinutes: z.int().nullable(),
    emergencyAvailable: z.boolean(),
    hourlyRateCents: z.int().nullable(),
    submittedAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
});
/** The name members see: the trading name if there is one. */
export function providerDisplayName(provider) {
    return provider.tradingName ?? provider.legalName;
}
//# sourceMappingURL=providers.js.map